/**
 * Thin mate-api caller contract for Hottie.
 *
 * mate-api owns LLM routing. This product keeps the Hottie system prompt,
 * persona, and context assembly, then passes them as input. No local
 * OpenRouter / Hermes / Grok client — if mate-api is registered but not
 * available, the fix is to configure OPENROUTER_API_KEY or OPENAI_API_KEY
 * there, not a second hop here.
 */

export const MATE_API_DEFAULT_URL = 'https://mate-platform-api.fly.dev';
export const HOTTR_SURFACE = 'hottr.app';

export const PASSPORT_JWT_REQUIRED = 'PASSPORT_JWT_REQUIRED';
export const PROVIDER_NOT_CONFIGURED = 'PROVIDER_NOT_CONFIGURED';

const UNAVAILABLE_CODES = new Set([
  PROVIDER_NOT_CONFIGURED,
  'MISSING_ENV',
  'MISSING_ENV_ANY',
  'CAPABILITY_DORMANT',
  'UNAVAILABLE',
]);

export type MateApiCapability = 'llm_routing' | 'text_generation';

export type MateApiFailure = {
  message: string;
  status: number;
  code: string;
};

export function capabilityFromPath(path: string): MateApiCapability {
  return path.includes('text_generation') ? 'text_generation' : 'llm_routing';
}

export function extractBearer(header: string | null | undefined): string | null {
  if (!header) return null;
  const m = /^Bearer\s+(.+)$/i.exec(header.trim());
  const token = m?.[1]?.trim();
  return token || null;
}

/**
 * Passport JWT preferred; dest user JWT is enough for the current Fly hop.
 * Missing Passport is not a client hard-stop. Does not mint DEST/OTP.
 */
export function mateJwtForRoute(
  passportJwt: string | null | undefined,
  destJwt: string | null | undefined,
): string | null {
  const passport = passportJwt?.trim() || '';
  if (passport) return passport;
  const dest = destJwt?.trim() || '';
  return dest || null;
}

export function hottieChatAuthHeaders(opts: {
  destAccessToken: string;
  passportAccessToken?: string | null;
  anonKey: string;
}): Record<string, string> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${opts.destAccessToken}`,
    apikey: opts.anonKey,
    'Content-Type': 'application/json',
  };
  const passport = opts.passportAccessToken?.trim();
  if (passport) headers['x-passport-authorization'] = `Bearer ${passport}`;
  return headers;
}

export function loudUnavailableMessage(capability: MateApiCapability, detail: string): string {
  return (
    `${capability} is registered on mate-api but not available (${detail}). ` +
    'Configure OPENROUTER_API_KEY or OPENAI_API_KEY on mate-api. ' +
    'This product does not keep a local LLM client.'
  );
}

export function isMateApiUnavailable(body: unknown, status: number): boolean {
  if (status === 404) return true;
  if (!body || typeof body !== 'object') return false;
  const obj = body as Record<string, unknown>;
  if (obj.available === false) return true;
  if (obj.registered === true && obj.available === false) return true;
  const code = typeof obj.code === 'string' ? obj.code : '';
  if (UNAVAILABLE_CODES.has(code)) return true;
  const reason = typeof obj.reason === 'string' ? obj.reason : '';
  return reason.includes('MISSING_ENV') || reason === 'CAPABILITY_DORMANT';
}

export function interpretMateApiFailure(
  path: string,
  status: number,
  body: unknown,
): MateApiFailure {
  const capability = capabilityFromPath(path);
  const obj = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  const code = typeof obj.code === 'string' && obj.code ? obj.code : '';
  const error = typeof obj.error === 'string' && obj.error ? obj.error : '';
  const reason = typeof obj.reason === 'string' && obj.reason ? obj.reason : '';
  const detail = code || error || reason || `HTTP ${status}`;

  if (isMateApiUnavailable(body, status)) {
    return {
      message: loudUnavailableMessage(capability, detail),
      status: 503,
      code: code || PROVIDER_NOT_CONFIGURED,
    };
  }

  return {
    message: error || `mate-api ${capability} failed (${status})`,
    status: status >= 400 && status < 600 ? status : 502,
    code: code || 'MATE_API_ERROR',
  };
}

export function passportJwtRequiredFailure(): MateApiFailure {
  return {
    message:
      'Passport JWT required to call mate-api. This product does not mint destination OTP or DEST tokens.',
    status: 401,
    code: PASSPORT_JWT_REQUIRED,
  };
}
